import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clone from 'lodash/clone';
import { Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

import HitCard from 'Components/Cards/HitCard';
import ItemList from 'Components/Product/Seo/ItemList';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import RelatedProductCard from 'Components/Product/ProductRelated/RelatedProductCard';
import { currencyCode } from 'Helpers/Geo/data';
import useViewport from 'Hooks/useViewport';

function ProductDLC({
  product,
  dlc,
  editions,
  editionTitle,
  title,
  description,
  intl: { formatMessage },
}) {
  const [filteredDLC, setFilteredDLC] = useState(dlc);
  const [sort, setSort] = useState('top_sellers');
  const { isBelowMd } = useViewport();
  let sortedResults = clone(dlc);

  const showHitCard = isBelowMd;

  useEffect(() => {
    /* eslint-disable max-len, no-nested-ternary, react-hooks/exhaustive-deps */
    switch (sort) {
      case 'top_sellers':
        setFilteredDLC(dlc);
        break;
      case 'release_date':
        sortedResults = sortedResults.sort((a, b) => ((a.release_date > b.release_date) ? 1 : ((b.release_date > a.release_date) ? -1 : 0)));
        setFilteredDLC(sortedResults.reverse());
        break;
      case 'name':
        sortedResults = sortedResults.sort((a, b) => ((a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)));
        setFilteredDLC(sortedResults);
        break;
      case 'price':
        sortedResults = sortedResults.sort((a, b) => ((a.price[currencyCode] > b.price[currencyCode]) ? 1 : ((b.price[currencyCode] > a.price[currencyCode]) ? -1 : 0)));
        setFilteredDLC(sortedResults);
        break;
      case 'priceHighLow':
        sortedResults = sortedResults.sort((a, b) => ((b.price[currencyCode] > a.price[currencyCode]) ? 1 : ((a.price[currencyCode] > b.price[currencyCode]) ? -1 : 0)));
        setFilteredDLC(sortedResults);
        break;
      case 'top_discount':
        sortedResults = sortedResults.sort((a, b) => ((b.discount_percent > a.discount_percent) ? 1 : ((a.discount_percent > b.discount_percent) ? -1 : 0)));
        setFilteredDLC(sortedResults);
        break;
      default:
    }
  }, [sort]);

  const messages = defineMessages({
    topSellers: {
      id: 'dlc.topSellers',
      defaultMessage: 'Top Sellers',
    },
    name: {
      id: 'dlc.name',
      defaultMessage: 'Alphabetical',
    },
    price: {
      id: 'dlc.price',
      defaultMessage: 'Price: Low to High',
    },
    priceHighLow: {
      id: 'dlc.priceHighLow',
      defaultMessage: 'Price: High to Low',
    },
    date: {
      id: 'dlc.release_date',
      defaultMessage: 'Release Date',
    },
    top_discount: {
      id: 'dlc.release_top_discount',
      defaultMessage: 'Top Discount',
    },
  });

  return (
    <React.Fragment>
      <div className="back-to-product-banner">
        <div className="container">
          <Link to={`/${product.type}/${product.slug}`}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <FormattedMessage
              id="product-dlc.back-to-product"
              defaultMessage="Back to {name}"
              values={{ name: product.name }}
            />
          </Link>
        </div>
      </div>
      <div className="product-dlc-container new-product-page pt-4">
        <div className="container">
          <h1 className="mb-3">{title}</h1>
          <p className="mb-4">{description}</p>

          <section className="d-flex justify-content-end mb-2">
            <div className="filters-container">
              <div className="dropdown-container sort-by">
                <select value={sort} onChange={e => setSort(e.target.value)}>
                  <option value="top_sellers">{formatMessage(messages.topSellers)}</option>
                  <option value="price">{formatMessage(messages.price)}</option>
                  <option value="priceHighLow">{formatMessage(messages.priceHighLow)}</option>
                  <option value="name">{formatMessage(messages.name)}</option>
                  <option value="top_discount">{formatMessage(messages.top_discount)}</option>
                  <option value="release_date">{formatMessage(messages.date)}</option>
                </select>
              </div>
            </div>
          </section>


          {showHitCard &&
            <section className="recommendation-container fan-grid-container page-section-margin with-dots">
              <ItemList title={title} hits={dlc} />
              {filteredDLC.map(hit => (
                <HitCard key={hit.product_id} hit={hit} headingTag="h2" />
              ))}
            </section>
          }

          {!showHitCard &&
            <section className="related-product-container page-section-margin with-dots">
              <ItemList title={title} hits={dlc} />
              {filteredDLC.map(hit => (
                <RelatedProductCard key={hit.product_id} hit={hit} headingTag="h2" />
              ))}
            </section>
          }

          {editions.length > 1 &&
            <section className="recommendation-container page-section-margin mt-6">
              <ItemList title={editionTitle} hits={editions} />
              <h2 className="mb-4">{editionTitle}</h2>
              <Row className="recommendation-container">
                {editions && editions.map(hit =>
                  <HitCard
                    key={hit.objectID}
                    hit={hit}
                  >
                    <PriceContainer
                      discountPercent={hit.discount_percent}
                      price={hit.price}
                      tiered={hit.tiered}
                      slug={hit.slug}
                      hideDiscount={hit.hide_discount}
                      payWhatYouWant={hit.pay_what_you_want}
                    />
                  </HitCard>,
                )}
              </Row>
            </section>
          }
        </div>
      </div>
    </React.Fragment>
  );
}

ProductDLC.propTypes = {
  product: PropTypes.shape({}).isRequired,
  dlc: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  editions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  editionTitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export default injectIntl(ProductDLC);
